import { useEffect, useState } from "react";
import { uniqBy } from "lodash";
import { Button } from "@/components/ui/Button";
import { getCorrectionResults } from "@/utils/exercises";
import {
  Submission,
  ExerciseOptionTypes,
  ExerciseGroupingsItem,
} from "@/types";
import GroupingIcon from "@/components/icons/exerciseV2/GroupingIcon";
import GrammarExerciseResult from "./GrammarExerciseResult";

interface WordButtonProps {
  children: React.ReactNode;
  showCorrection: boolean;
  correctionResults?: CorrectionResult[];
  isSelected?: boolean;
  onSelect?: (word: string) => void;
}

interface CorrectionResult {
  word: string;
  isCorrect: boolean;
}

interface DroppableContainerProps {
  onDrop: (name: string) => void;
  collectedItems: string[];
  label: string;
  showCorrection: boolean;
  correctionResults: CorrectionResult[];
  isActive?: boolean;
  selectedWord: string;
  onSelect: (word: string) => void;
}

interface GroupingExerciseProps {
  data: ExerciseGroupingsItem[];
}

interface SubmissionDataItem {
  questionId: number;
  textAnswer: string;
  selectedOptionId?: number;
  isCorrect?: boolean;
}

interface CategoryState {
  name: string;
  words: string[];
}

interface CorrectionResult {
  word: string;
  isCorrect: boolean;
}

const WordButton: React.FC<WordButtonProps> = ({
  children,
  showCorrection,
  correctionResults,
  isSelected,
  onSelect,
}) => {
  const isCorrect = correctionResults?.find(
    (result) => result.word === children,
  )?.isCorrect;

  const backgroundColorClass = showCorrection
    ? isCorrect
      ? "bg-[#219653]"
      : "bg-[#C80505]"
    : isSelected
    ? "bg-brand-secondary"
    : "bg-brand-secondary";

  return (
    <button
      onClick={() =>
        !showCorrection && onSelect && onSelect?.(children as string)
      }
      className={`flex justify-center px-3 py-1 ${backgroundColorClass} text-white text-[10px] rounded-md 
        ${
          showCorrection
            ? "cursor-default"
            : "cursor-pointer active:scale-95 transition-transform"
        }`}
    >
      {children}
    </button>
  );
};

const DroppableContainer: React.FC<DroppableContainerProps> = ({
  onDrop,
  collectedItems,
  label,
  showCorrection,
  correctionResults,
  isActive,
  selectedWord,
  onSelect,
}) => {
  return (
    <button
      onClick={() => !showCorrection && isActive && onDrop("")}
      className={`w-full flex flex-col flex-1 pb-4 rounded-2xl border min-h-[7rem] ${
        isActive ? "border-brand-secondary border-2" : "border-brand-secondary"
      } border-solid mb-2.5 ${!showCorrection ? "cursor-pointer" : ""}`}
    >
      <h2 className="w-full text-center text-[10px] py-1 text-white bg-brand-secondary rounded-t-xl">
        {label.toUpperCase()}
      </h2>

      <div className="flex flex-wrap gap-1.5 self-start m-1.5">
        {collectedItems.map((item) => (
          <WordButton
            key={item}
            showCorrection={showCorrection}
            correctionResults={correctionResults}
            isSelected={selectedWord === item}
            onSelect={() => (onDrop(item), onSelect?.(item))}
          >
            {item}
          </WordButton>
        ))}
      </div>
    </button>
  );
};

function shuffleArray(array: string[]) {
  let currentIndex = array.length,
    randomIndex;

  while (currentIndex !== 0) {
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex--;

    [array[currentIndex], array[randomIndex]] = [
      array[randomIndex],
      array[currentIndex],
    ];
  }

  return array;
}

const GroupingExercise: React.FC<GroupingExerciseProps> = ({ data }) => {
  const [showCorrection, setShowCorrection] = useState<boolean>(false);
  const [showResults, setShowResults] = useState(false);
  const [correctAnswersCount, setCorrectAnswersCount] = useState(0);
  const [totalWords, setTotalWords] = useState(0);
  const [submissionData, setSubmissionData] = useState<SubmissionDataItem[]>(
    [],
  );
  const [categories, setCategories] = useState<CategoryState[]>([]);
  const [uncategorizedWords, setUncategorizedWords] = useState<string[]>([]);
  const [isShuffled, setIsShuffled] = useState(false);
  const [selectedWord, setSelectedWord] = useState<string | null>(null);

  useEffect(() => {
    if (data.length > 0 && !isShuffled) {
      // Group data by category
      const categoryGroups = data.reduce(
        (acc, item) => {
          if (!acc[item.category]) {
            acc[item.category] = [];
          }
          acc[item.category].push(item.word);
          return acc;
        },
        {} as Record<string, string[]>,
      );

      // Initialize categories state
      setCategories(
        Object.entries(categoryGroups).map(([name]) => ({
          name,
          words: [],
        })),
      );

      // Get all words and shuffle them
      const allWords = data.map((item) => item.word);
      setUncategorizedWords(shuffleArray([...allWords]));
      setIsShuffled(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (data.length > 0) {
      const totalWordsTransformed = data.reduce((acc, currentCategoryData) => {
        const wordsTransformed = currentCategoryData.word
          .split(",")
          .map((word) => word.trim());
        return acc + wordsTransformed.length;
      }, 0);

      setTotalWords(totalWordsTransformed);
    }
  }, [data]);

  const handleShowCorrection = () => {
    setShowCorrection(true);

    // Create a mapping of correct words for each category
    const correctWordsMapping = data.reduce<Record<string, string[]>>(
      (acc, current) => {
        if (!acc[current.category]) {
          acc[current.category] = [];
        }
        acc[current.category].push(current.word);
        return acc;
      },
      {},
    );

    // Check correctness for all categories
    let totalCorrectAnswers = 0;
    categories.forEach((category) => {
      const correctWordsForCategory = correctWordsMapping[category.name] || [];
      const correctWordsCount = category.words.filter((word) =>
        correctWordsForCategory.includes(word),
      ).length;
      totalCorrectAnswers += correctWordsCount;
    });

    setCorrectAnswersCount(totalCorrectAnswers);

    // Prepare submission data
    const newSubmissionData: SubmissionDataItem[] = data.map(
      (categoryData) => ({
        questionId: categoryData.id,
        textAnswer:
          categories
            .find((cat) => cat.name === categoryData.category)
            ?.words.join(",") || "",
        isCorrect: categories
          .find((cat) => cat.name === categoryData.category)
          ?.words.includes(categoryData.word),
      }),
    );

    setSubmissionData(newSubmissionData);
  };

  const handleDrop = (word: string, targetCategory: string) => {
    setCategories((prev) =>
      prev.map((cat) => {
        // Remove word from other categories if it exists
        if (cat.name !== targetCategory && cat.words.includes(word)) {
          return { ...cat, words: cat.words.filter((w) => w !== word) };
        }

        // Add word to target category
        if (cat.name === targetCategory && !cat.words.includes(word)) {
          return { ...cat, words: [...cat.words, word] };
        }
        return cat;
      }),
    );
    setUncategorizedWords((prev) => prev.filter((w) => w !== word));
  };

  const handleWordSelect = (word: string) => {
    setSelectedWord(selectedWord === word ? null : word);
  };

  const handleCategoryClick = (categoryName: string) => {
    if (selectedWord) {
      handleDrop(selectedWord, categoryName);
      setSelectedWord(null);
    }
  };

  const handleShowMyScore = () => {
    setShowResults(true);
  };

  if (showResults) {
    return (
      <GrammarExerciseResult
        onClose={() => setShowResults(false)}
        data={{
          exerciseType: ExerciseOptionTypes.Grouping,
          score: correctAnswersCount,
          total: totalWords,
          submissionData: submissionData as Submission[],
        }}
      />
    );
  }

  return (
    <div className="pb-4">
      <div className="flex flex-col gap-2 px-5 pt-5 bg-white rounded-lg border border-brand-secondary border-solid">
        <header className="flex gap-5 justify-between w-full">
          <div className="flex gap-2 justify-between items-center text-xl leading-3 text-black">
            <GroupingIcon className="md:w-4 md:h-4" />

            <div className="w-full ">
              <p className="text-[12px] font-bold ">Grouping</p>
              <p className="text-[10px] leading-[10px] font-normal text-black">
                Select a word, then click on the group where you want to place
                it.
              </p>
            </div>
          </div>
        </header>

        <div>
          <section className="text-sm leading-4 min-h-[200px]">
            <div className="flex flex-col">
              <div className="flex items-start w-full text-sm leading-4 text-center text-white whitespace-nowrap rounded-md min-h-[80px]">
                <div className="flex flex-wrap gap-1.5 self-start mt-2.5">
                  {uniqBy(uncategorizedWords, (word) => word).map((word) => (
                    <WordButton
                      key={word}
                      showCorrection={showCorrection}
                      correctionResults={[]}
                      isSelected={selectedWord === word}
                      onSelect={handleWordSelect}
                    >
                      {word}
                    </WordButton>
                  ))}
                </div>
              </div>

              <main className="mt-2.5 w-full">
                {/* Categories */}
                <div className="grid grid-cols-1 gap-4">
                  {categories.map((category) => (
                    <DroppableContainer
                      key={category.name}
                      onDrop={() => handleCategoryClick(category.name)}
                      collectedItems={category.words}
                      label={category.name}
                      showCorrection={showCorrection}
                      correctionResults={getCorrectionResults(
                        category.name,
                        category.words,
                        data,
                      )}
                      isActive={!!selectedWord}
                      selectedWord={selectedWord ?? ""}
                      onSelect={handleWordSelect}
                    />
                  ))}
                </div>
              </main>
            </div>
          </section>
        </div>
      </div>

      {!showCorrection && (
        <Button
          className="mx-auto mt-4 text-[10px] rounded-[10px] w-full border-black bg-white hover:bg-white hover:text-black"
          variant="outline"
          onClick={handleShowCorrection}
          disabled={uncategorizedWords.length > 0 || showCorrection}
        >
          Correct now
        </Button>
      )}

      {showCorrection && (
        <Button
          variant="default"
          className="mx-auto mt-4 text-[10px] hover:bg-brand-secondary w-full rounded-[10px]"
          onClick={handleShowMyScore}
        >
          My Score ⭐
        </Button>
      )}
    </div>
  );
};
export default GroupingExercise;
